import { Component } from '@theme/component';

/**
 * Careers listing component
 *
 * Client-side filtering over server-rendered job nodes. No fetching: every job is
 * rendered up front (card + detail panel) and this component only toggles visibility
 * and selection.
 *
 * State lives on the instance, so two sections on one page never collide.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [list]
 * @property {HTMLButtonElement[]} [cards]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement} [detail]
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [empty]
 * @property {HTMLInputElement} [keyword]
 * @property {HTMLSelectElement} [departmentSelect]
 * @property {HTMLSelectElement} [locationSelect]
 * @property {HTMLElement[]} [bubbles]
 * @property {HTMLButtonElement} [clear]
 *
 * @extends {Component<Refs>}
 */
class CareersListing extends Component {
  /** @type {{ keyword: string, department: string, location: string, types: Set<string> }} */
  #state = { keyword: '', department: '', location: '', types: new Set() };

  /**
   * Below this width the results grid collapses to one column, so the detail panel
   * moves inline beneath its own card instead of sitting in a second column.
   */
  #mobile = window.matchMedia('(max-width: 989px)');

  /** Last known breakpoint, so resize handling can bail out when nothing changed. */
  #wasMobile = window.matchMedia('(max-width: 989px)').matches;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('submit', this.#handleSubmit);

    // Outside click closes any open bubble panel.
    this.#onDocumentClick = (event) => {
      if (!(event.target instanceof Node)) return;
      if (this.contains(event.target)) return;
      this.#closeAllBubbles();
    };
    document.addEventListener('click', this.#onDocumentClick);
    this.#mobile.addEventListener('change', this.#handleBreakpoint);
    // Fallback: some environments resize the viewport without emitting a matchMedia
    // change event. The guard makes this a no-op unless the breakpoint really flipped.
    window.addEventListener('resize', this.#handleBreakpoint, { passive: true });

    this.#apply();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    this.#mobile.removeEventListener('change', this.#handleBreakpoint);
    window.removeEventListener('resize', this.#handleBreakpoint);
  }

  /** Crossing the breakpoint re-homes the open panel and swaps the ARIA model. */
  #handleBreakpoint = () => {
    if (this.#mobile.matches === this.#wasMobile) return;
    this.#wasMobile = this.#mobile.matches;

    this.#syncRoles();
    this.#reflowPanels();
  };

  /** @type {(event: MouseEvent) => void} */
  #onDocumentClick = () => {};

  get #cards() {
    return this.refs.cards ?? [];
  }

  get #panels() {
    return this.refs.panels ?? [];
  }

  get #bubbles() {
    return this.refs.bubbles ?? [];
  }

  /* ------------------------------------------------------------------ events */

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-bubble-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      this.#toggleBubble(trigger);
      return;
    }

    const option = target.closest('[data-filter-option]');
    if (option instanceof HTMLButtonElement) {
      this.#chooseOption(option);
      return;
    }

    const card = target.closest('[data-job-card]');
    if (card instanceof HTMLElement) {
      this.#select(card.dataset.jobId ?? '');
      return;
    }

    if (target.closest('[data-clear-filters]')) {
      this.#clearAll();
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (event.target === this.refs.keyword) {
      this.#state.keyword = this.refs.keyword?.value.trim().toLowerCase() ?? '';
      this.#apply();
    }
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (target === this.refs.departmentSelect) {
      this.#state.department = this.refs.departmentSelect?.value ?? '';
      this.#syncBubblesFromState();
      this.#apply();
    } else if (target === this.refs.locationSelect) {
      this.#state.location = this.refs.locationSelect?.value ?? '';
      this.#syncBubblesFromState();
      this.#apply();
    }
  };

  /** The search bar is a form so Enter submits; there is nothing to post. */
  /** @param {Event} event */
  #handleSubmit = (event) => {
    event.preventDefault();
    this.#apply();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (event.key === 'Escape') {
      const openTrigger = this.#bubbles
        .map((b) => b.querySelector('[data-bubble-trigger]'))
        .find((t) => t instanceof HTMLButtonElement && t.getAttribute('aria-expanded') === 'true');

      if (openTrigger instanceof HTMLButtonElement) {
        event.stopPropagation();
        this.#closeAllBubbles();
        openTrigger.focus();
      }
      return;
    }

    // Roving focus inside an open bubble panel.
    const panel = target.closest('[data-bubble-panel]');
    if (panel instanceof HTMLElement && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const options = /** @type {HTMLButtonElement[]} */ ([...panel.querySelectorAll('[data-filter-option]')]);
      const index = options.indexOf(/** @type {HTMLButtonElement} */ (target.closest('[data-filter-option]')));
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      options[(next + options.length) % options.length]?.focus();
      return;
    }

    // Cards are role="option" divs, not buttons, so activation keys are explicit.
    const activated = target.closest('[data-job-card]');
    if (activated instanceof HTMLElement && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.#select(activated.dataset.jobId ?? '');
      return;
    }

    // Arrow navigation across the job list.
    const card = target.closest('[data-job-card]');
    if (card instanceof HTMLElement && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const visible = this.#cards.filter((c) => !c.hidden);
      const index = visible.indexOf(/** @type {HTMLButtonElement} */ (card));
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      const target2 = visible[(next + visible.length) % visible.length];
      if (target2) {
        target2.focus();
        this.#select(target2.dataset.jobId ?? '');
      }
    }
  };

  /* ----------------------------------------------------------------- bubbles */

  /** @param {HTMLButtonElement} trigger */
  #toggleBubble(trigger) {
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';
    this.#closeAllBubbles();

    if (isOpen) return;

    trigger.setAttribute('aria-expanded', 'true');
    const panel = trigger.parentElement?.querySelector('[data-bubble-panel]');
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
      /** @type {HTMLElement | null} */ (panel.querySelector('[data-filter-option]'))?.focus();
    }
  }

  #closeAllBubbles() {
    for (const bubble of this.#bubbles) {
      bubble.querySelector('[data-bubble-trigger]')?.setAttribute('aria-expanded', 'false');
      const panel = bubble.querySelector('[data-bubble-panel]');
      if (panel instanceof HTMLElement) panel.hidden = true;
    }
  }

  /** @param {HTMLButtonElement} option */
  #chooseOption(option) {
    const group = option.dataset.filterOption ?? '';
    const value = option.dataset.value ?? '';

    if (group === 'type') {
      if (this.#state.types.has(value)) this.#state.types.delete(value);
      else this.#state.types.add(value);
    } else if (group === 'location') {
      this.#state.location = this.#state.location === value ? '' : value;
      if (this.refs.locationSelect) this.refs.locationSelect.value = this.#state.location;
      this.#closeAllBubbles();
    } else if (group === 'department') {
      this.#state.department = this.#state.department === value ? '' : value;
      if (this.refs.departmentSelect) this.refs.departmentSelect.value = this.#state.department;
      this.#closeAllBubbles();
    }

    this.#syncBubblesFromState();
    this.#apply();
  }

  #clearAll() {
    this.#state = { keyword: '', department: '', location: '', types: new Set() };
    if (this.refs.keyword) this.refs.keyword.value = '';
    if (this.refs.departmentSelect) this.refs.departmentSelect.value = '';
    if (this.refs.locationSelect) this.refs.locationSelect.value = '';
    this.#closeAllBubbles();
    this.#syncBubblesFromState();
    this.#apply();
  }

  /** Reflect state into every bubble: label, active border, count badge, checkmarks. */
  #syncBubblesFromState() {
    for (const bubble of this.#bubbles) {
      const group = bubble.dataset.filterGroup ?? '';
      const trigger = bubble.querySelector('[data-bubble-trigger]');
      const label = bubble.querySelector('[data-bubble-label]');
      const badge = bubble.querySelector('[data-bubble-badge]');
      let active = false;

      if (group === 'type') {
        active = this.#state.types.size > 0;
        if (badge instanceof HTMLElement) {
          badge.hidden = !active;
          badge.textContent = String(this.#state.types.size);
        }
      } else {
        const value = group === 'location' ? this.#state.location : this.#state.department;
        active = value !== '';
        if (label instanceof HTMLElement) {
          label.textContent = active ? value : (label.dataset.defaultLabel ?? label.textContent);
        }
      }

      trigger?.classList.toggle('careers-bubble__trigger--active', active);

      for (const option of bubble.querySelectorAll('[data-filter-option]')) {
        const value = /** @type {HTMLElement} */ (option).dataset.value ?? '';
        const selected =
          group === 'type'
            ? this.#state.types.has(value)
            : value === (group === 'location' ? this.#state.location : this.#state.department);

        option.classList.toggle('careers-option--selected', selected);
        option.setAttribute('aria-pressed', String(selected));
      }
    }

    const anyActive =
      this.#state.keyword !== '' ||
      this.#state.department !== '' ||
      this.#state.location !== '' ||
      this.#state.types.size > 0;

    if (this.refs.clear) this.refs.clear.hidden = !anyActive;
  }

  /* --------------------------------------------------------------- filtering */

  /** @param {HTMLElement} card */
  #matches(card) {
    const { keyword, department, location, types } = this.#state;
    const data = card.dataset;

    if (keyword && !(data.title ?? '').toLowerCase().includes(keyword)) return false;
    if (department && data.department !== department) return false;
    if (location && data.location !== location) return false;
    if (types.size > 0 && !types.has(data.jobType ?? '')) return false;

    return true;
  }

  #apply() {
    let visible = 0;
    /** @type {HTMLElement | null} */
    let first = null;

    for (const card of this.#cards) {
      const show = this.#matches(card);
      card.hidden = !show;
      if (show) {
        visible += 1;
        first ??= card;
      }
    }

    // Count line.
    if (this.refs.count) {
      const noun = visible === 1 ? this.dataset.labelPositionSingular : this.dataset.labelPositionPlural;
      this.refs.count.textContent = `${visible} ${noun ?? ''}`.trim();
    }

    // Empty state. Detail-column visibility is owned by #reflowPanels.
    const hasResults = visible > 0;
    if (this.refs.empty) this.refs.empty.hidden = hasResults;
    if (this.refs.count) this.refs.count.hidden = !hasResults;

    // Keep a valid selection: if the selected job was filtered out, take the first visible one.
    // Checks the class, not aria-*, because the ARIA attribute differs per breakpoint.
    const selected = this.#cards.find((card) => card.classList.contains('careers-card--selected'));
    if (!hasResults) {
      this.#showPanel('');
    } else if (!selected || selected.hidden) {
      this.#select(first?.dataset.jobId ?? '');
    } else {
      // Selection unchanged, but roles and panel placement still need to match the
      // current breakpoint — this is the path taken on first load.
      this.#syncRoles();
      this.#reflowPanels();
    }

    this.#syncBubblesFromState();
  }

  /* --------------------------------------------------------------- selection */

  /** @param {string} id */
  #select(id) {
    if (!id) return;

    for (const card of this.#cards) {
      const isMatch = card.dataset.jobId === id;
      card.classList.toggle('careers-card--selected', isMatch);
      card.tabIndex = isMatch ? 0 : -1;
    }

    this.#syncRoles();
    this.#showPanel(id);
  }

  /** @param {string} id */
  #showPanel(id) {
    for (const panel of this.#panels) {
      panel.hidden = panel.dataset.jobId !== id;
    }
    this.#reflowPanels();
  }

  /**
   * Desktop is a listbox of options driving a separate detail pane. Mobile is a set of
   * disclosures, each revealing its own panel — so the roles have to swap with the
   * layout, otherwise a panel would sit inside a listbox as a non-option child.
   */
  #syncRoles() {
    const isMobile = this.#mobile.matches;
    const list = this.refs.list;

    if (list) list.setAttribute('role', isMobile ? 'presentation' : 'listbox');

    for (const card of this.#cards) {
      const isSelected = card.classList.contains('careers-card--selected');

      if (isMobile) {
        card.setAttribute('role', 'button');
        card.removeAttribute('aria-selected');
        card.setAttribute('aria-expanded', String(isSelected));
      } else {
        card.setAttribute('role', 'option');
        card.removeAttribute('aria-expanded');
        card.setAttribute('aria-selected', String(isSelected));
      }
    }
  }

  /**
   * Re-homes panels: on mobile the open one moves directly after its card, everything
   * else lives in the detail column.
   */
  #reflowPanels() {
    const isMobile = this.#mobile.matches;
    const detail = this.refs.detail;

    for (const panel of this.#panels) {
      const isOpen = !panel.hidden;
      const inline = isMobile && isOpen;

      panel.classList.toggle('careers-panel--inline', inline);

      if (inline) {
        const card = this.#cards.find((c) => c.dataset.jobId === panel.dataset.jobId);
        if (card && card.nextElementSibling !== panel) card.after(panel);
      } else if (detail && panel.parentElement !== detail) {
        detail.appendChild(panel);
      }
    }

    if (detail) {
      const hasPanelInColumn = this.#panels.some((p) => !p.hidden && p.parentElement === detail);
      detail.hidden = isMobile || !hasPanelInColumn;
    }
  }
}

if (!customElements.get('careers-listing')) {
  customElements.define('careers-listing', CareersListing);
}
